
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, FormControlLabel, IconButton, Input, InputAdornment, InputBase, ListItemIcon, ListItemText, makeStyles, MenuItem, Paper, Radio, RadioGroup, Select, SvgIcon, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import PencilIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import clsx from "clsx";
import { createRef, forwardRef, memo, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../hooks/useAlert";
import { Loading } from "../../parts/Loading";
import { useCustomerPointList } from "../../store/PointAreaSettingState";
import * as FPEUtils from "../../utils/FPEUtils";
import Pagination from '@material-ui/lab/Pagination';
import { timeZoneSelector } from "../../store/TimeZoneState";
import { useRecoilValue } from "recoil";
import moment from "moment";
import CoordinateButton from "../flightplan/CoordinateButton";
import { DATA_HAS_BEEN_CHANGED_ERROR } from "../../utils/LinksAPI";
import { POINT_DEFAULT_ICONS } from "./PointAreaSettingIconList";
import RoleControl from "../RoleControl";
import { SUBJ_POINT_AREA_SETTING_LIST, VERB_CREATE, VERB_DELETE, VERB_UPDATE } from "../../utils/AccessControl";

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	hide: {
		display: 'none',
	},
	flexColumn: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	flexVariable: {
		overflow: 'hidden',
		flexGrow: 1,
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		'&:nth-child(2)': {
			position: 'sticky',
			left: '34px',
			zIndex: 2,
			// fontWeight: 'bold',
			whiteSpace: 'nowrap',
		},
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+2)': {
			zIndex: 3
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	iconCell: {
		width: '32px',
		minWidth: '32px',
		maxWidth: '32px',
		verticalAlign: 'middle',
	},
	imageCell: {
		padding: '4px',
		width: '72px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	newline: {
		whiteSpace: 'break-spaces',
	},
	alignRight: {
		textAlign: 'right',
	},
	pagination: {
		'& .MuiTablePagination-toolbar': {
			minHeight: 'auto',
		},
		'& .MuiIconButton-root': {
			padding: '6px',
		},
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		// '&:not(:first-child)': {
		// 	marginTop: '20px',
		// },
		'& .required': {
			color: '#f44336',
		},
		'& input[type=color]': {
			width: '50px',
			height: '30px',
		},
	},
	formInputLonLat: {
		maxWidth: '12em',
	},
	formRoot: {
		fontSize: '75%',
		'& .required': {
			color: '#f44336',
		},
		'& dl:not(:first-child)': {
			marginTop: '20px',
		},
		'& dt': {
			marginBottom: '5px',
		},
		'& #iconId-checkbox': {
			display: 'flex',
			minWidth: '10rem',
		},
	},
	formHorizontally: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
	},
	formSpacer: {
		'& > *:not(:first-of-type)': { 
			marginLeft: '20px'
		},
	},
	formHeading: {
		marginRight: '10px',
	},
	menuItem: {
		paddingTop: 0,
		paddingBottom: 0,
	},
	menuItemCheckbox: {
		padding: 4,
	},
	menuItemText: {
		marginLeft: 4,
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
	},
	chip: {
		margin: 2,
	},
	dragImageBox: {
		border: '3px dashed rgba(128, 128, 128, 0.5)',
		borderRadius: '2px',
		'& *': {
			cursor: 'pointer',
		}
	},
	dragImageBoxLabel: {
		fontSize: '75%',
		margin: '-6px 0 2px 14px',
	},
	dragImageBoxActive: {
		borderColor: 'rgba(80, 80, 255, 0.8)',
	},
	dragImageTextBox: {
		width: '210px',
		height: '140px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		padding: '10px',
		fontSize: '15px',
		whiteSpace: 'break-spaces',
	},
});

const DEF_TYPES = {
	1: 'Point',
	2: 'Circle',
	3: 'Polygon',
};

const DEF_ICON_DISPLAY = {
	1: 'Always',
	2: 'Zoom',
};

const DEF_LABEL_DISPLAY = {
	1: 'Always',
	2: 'Hover',
};

const formatNM = v => Math.round(FPEUtils.m2nm(v) * 10000) / 10000;

const columns = [
	{ id: 'name', label: 'Name' },
	{ id: 'type', label: 'point_area_type', format: (v, t) => t(DEF_TYPES[v]) },
	{ id: 'iconName', label: 'Icon', colSpan: 2 },
	{ id: 'iconDisplay', format: (v, t) => t(DEF_ICON_DISPLAY[v]) },
	{ id: 'color', label: 'Color', format: v => {
		return (<Box style={{ width: '16px', height: '16px', backgroundColor: `#${v}`, border: '1px solid black' }} title={`#${v}`}></Box>);
	} },
	{ id: 'concentricCircles', label: 'Concentric circles', format: v => v && v.slice().sort((a, b) => a - b).map(v => formatNM(v)).join(',') },
	{ id: 'lat', label: 'latitude', format: v => v && !Array.isArray(v[0]) && FPEUtils.formatLatDM(v[1]) },
	{ id: 'lon', label: 'longitude', format: v => v && !Array.isArray(v[0]) && FPEUtils.formatLonDM(v[0]) },
	{ id: 'radius', label: 'Radius [NM]', format: v => v && formatNM(v) },
	{ id: 'labelDisplay', label: 'Labeling', format: (v, t) => t(DEF_LABEL_DISPLAY[v]) },
	{ id: 'iconTermBegin', label: 'Start', format: v => v && FPEUtils.formatTimeZoneDate(v) },
	{ id: 'iconTermEnd', label: 'End', format: v => v && FPEUtils.formatTimeZoneDate(v) },
];

const formInputList = [
	{
		code: 'name',
		name: 'Name',
		type: 'text',
		rules: {
			required: true,
			pattern: /^[^'"!@#$%^&*();:{}[\]>? 　]{1,20}$/,
			maxLength: 20,
			unique: true,
			reedit: true,
		},
		default: ""
	},
	{
		code: 'type',
		name: 'point_area_type',
		type: 'radio',
		data: {
			list: Object.entries(DEF_TYPES).map(([id, name]) => ({id, name})),
			key: 'id',
			name: 'name',
		},
		rules: {
			required: true,
			reedit: false,
		},
		default: "1",
	},
	{
		code: 'iconId',
		name: 'Icon',
		type: 'iconselect',
		data: {
			list: 'iconListAll',
			key: 'iconId',
			name: 'name',
		},
		rules: {
			validate: {
				required: (value, {getValues}) => {
					if (getValues('type') === "1") {
						return value !== ''
					}
					return true;
				}
			}
		},
		default: '',
	},
	{
		code: 'iconDisplay',
		name: 'iconDisplay',
		type: 'radio',
		data: {
			list: Object.entries(DEF_ICON_DISPLAY).map(([id, name]) => ({id, name})),
			key: 'id',
			name: 'name',
		},
		rules: {
			validate: {
				required: (value, {getValues}) => {
					if (getValues('type') === "1") {
						return value !== null
					}
					return true;
				}
			}
		},
		default: '1',
	},
	{
		code: 'color',
		name: 'Color',
		type: 'color',
		rules: {
			required: true,
			pattern: /^#[0-9a-fA-F]{6}$/,
			maxLength: 7,
		},
		default: "#000000"
	},
	{
		code: 'concentricCircles',
		name: 'Concentric circles',
		type: 'switch',
		data: {
			list: 'iconListAll',
			key: 'iconId',
			name: 'name',
		},
		rules: {},
		default: false,
	},
	{
		code: 'concentricCircles1',
		name: 'Concentric circles1',
		type: 'text',
		rules: {
			validate: {
				required: (value, {getValues, t}) => {
					if (getValues('type') !== "1") {
						return true;
					}
					if (getValues('concentricCircles')) {
						return value !== '' || t('validate_required')
					}
					return true;
				},
				pattern: (value, {getValues, t}) => {
					if (getValues('type') !== "1") {
						return true;
					}
					if (!getValues('concentricCircles')) {
						return true;
					}
					if (value === "") {
						return true;
					}
					if (!/^[0-9]+(\.[0-9]+)?$/.test(value)) {
						return t('validate_pattern');
					}
					if (!/^[0-9]{1,5}(\.[0-9]{1,4})?$/.test(value)) {
						return t('validate_number_out_of_range', {min: 0.0001, max: 99999.9999});
					}
				},
			}
		},
		unit: 'NM',
		default: ""
	},
	{
		code: 'concentricCircles2',
		name: 'Concentric circles2',
		type: 'text',
		rules: {
			validate: {
				pattern: (value, {getValues, t}) => {
					if (getValues('type') !== "1") {
						return true;
					}
					if (!getValues('concentricCircles')) {
						return true;
					}
					if (value === "") {
						return true;
					}
					if (!/^[0-9]+(\.[0-9]+)?$/.test(value)) {
						return t('validate_pattern');
					}
					if (!/^[0-9]{1,5}(\.[0-9]{1,4})?$/.test(value)) {
						return t('validate_number_out_of_range', {min: 0.0001, max: 99999.9999});
					}
				},
			}
		},
		unit: 'NM',
		default: ""
	},
	{
		code: 'concentricCircles3',
		name: 'Concentric circles3',
		type: 'text',
		rules: {
			validate: {
				pattern: (value, {getValues, t}) => {
					if (getValues('type') !== "1") {
						return true;
					}
					if (!getValues('concentricCircles')) {
						return true;
					}
					if (value === "") {
						return true;
					}
					if (!/^[0-9]+(\.[0-9]+)?$/.test(value)) {
						return t('validate_pattern');
					}
					if (!/^[0-9]{1,5}(\.[0-9]{1,4})?$/.test(value)) {
						return t('validate_number_out_of_range', {min: 0.0001, max: 99999.9999});
					}
				},
			}
		},
		unit: 'NM',
		default: ""
	},
	{
		code: 'lat',
		name: 'latitude',
		type: 'text',
		rules: {
			required: true,
			pattern: /^(\+|-)?[0-9]{1,2}(\.[0-9]+)?$/,
			maxLength: 20,
		},
		default: null
	},
	{
		code: 'lon',
		name: 'longitude',
		type: 'text',
		rules: {
			required: true,
			pattern: /^(\+|-)?[0-9]{1,3}(\.[0-9]+)?$/,
			maxLength: 20,
		},
		default: null
	},
	{
		code: 'radius',
		name: 'Radius',
		type: 'text',
		rules: {
			validate: {
				required: (value, {getValues, t}) => {
					if (getValues('type') === "2") {
						return value !== '' || t('validate_required')
					}
					return true;
				},
				pattern: (value, {getValues, t}) => {
					if (getValues('type') !== "2") {
						return true;
					}
					if (!/^[0-9]+(\.[0-9]+)?$/.test(value)) {
						return t('validate_pattern');
					}
					if (!/^[0-9]{1,5}(\.[0-9]{1,4})?$/.test(value)) {
						return t('validate_number_out_of_range', {min: 0.0001, max: 99999.9999});
					}
				},
			}
		},
		unit: 'NM',
		default: ""
	},
	{
		code: 'labelDisplay',
		name: 'Labeling',
		type: 'radio',
		data: {
			list: Object.entries(DEF_LABEL_DISPLAY).map(([id, name]) => ({id, name})),
			key: 'id',
			name: 'name',
		},
		rules: {
			required: true,
		},
		default: '1',
	},
	{
		code: 'iconTermBegin',
		name: 'Start',
		type: 'datetime-local',
		rules: {
		},
		default: FPEUtils.formatTimeZoneDate(Date.now()),
	},
	{
		code: 'iconTermEnd',
		name: 'End',
		type: 'datetime-local',
		rules: {
			validate: {
				timeseries: (value, {getValues, t}) => {
					return getValues('iconTermBegin') < value || t('Please enter a start datetime or later.')
				},
			},
		},
		default: FPEUtils.formatTimeZoneDate("2100/12/31 00:00Z"),
	},
];

const formDefault = formInputList.reduce((a, v) => {
	if ("default" in v) {
		a[v.code] = v.default;
	} else {
		a[v.code] = "";
	}
	return a;
}, {});

const formInputItem = code => {
	return formInputList.find(elem => elem.code === code) || {
		code,
		rules: {}
	};
};

const PointAreaListTable = memo(forwardRef(({filterWord, handleEdit, setWaiting, tableRef}, ref) => {
	const {
		customerPointList,
		customerIconList,
		registerCustomerPoint,
		updateCustomerPoint,
		deleteCustomerPoint,
		refreshCustomerPointList
	} = useCustomerPointList();
	const [del, setDel] = useState();
	const { showError, showWarning } = useAlert();
	const [page, setPage] = useState(0);
	const [rowsPerPage/*, setRowsPerPage*/] = useState(100);

	const classes = useStyles();
	const {t} = useTranslation();

	const handleDelete = (row) => {
		setDel(row);
		setOpen(true);
	}

	const sortedCustomerPointList = useMemo(() => {
		return Array.from(customerPointList).sort((a, b) => a.name.localeCompare(b.name));
	}, [customerPointList]);

	const iconListAll = useMemo(() => {
		return POINT_DEFAULT_ICONS.map(v => ({...v, name: t(v.name)}))
			.concat(Array.from(customerIconList).sort((a, b) => a.name.localeCompare(b.name)));
	}, [customerIconList, t]);

	const list = useMemo(() => {
		setPage(0);
		if (filterWord === "") {
			return sortedCustomerPointList;
		}
		return sortedCustomerPointList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					case 'lat':
					case 'lon':
						return (col.format(row['position']) || "").indexOf(filterWord) !== -1;
					default:
						const cell = row[col.id] && ((col.format && col.format(row[col.id], t)) || row[col.id]);
						return cell !== undefined && cell !== null && String(cell).indexOf(filterWord) !== -1;
				}
			});
		});
	}, [sortedCustomerPointList, filterWord, setPage, t]);

	useEffect(() => {
		return () => {
			refreshCustomerPointList();
		}
	}, [refreshCustomerPointList]);

	useImperativeHandle(ref, () => ({
		iconListAll,
		register: (registerData) => {
			setWaiting(true);
			registerCustomerPoint(registerData)
				.then(() => {
					// console.log("register comp");
				})
				.catch(e => {
					console.error("register error", e);
					showError(`${t('Register failed')}`);
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		update: (updateData) => {
			setWaiting(true);
			updateCustomerPoint(updateData)
				.then(() => {
					// console.log("update comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("update failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshCustomerPointList();
					} else {
						console.error("update error", e);
						showError(`${t('update_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		delete: (deleteData) => {
			setWaiting(true);
			deleteCustomerPoint(deleteData)
				.then(() => {
					// console.info("delete comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("delete failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshCustomerPointList();
					} else {
						console.error("delete error", e);
						showError(`${t('delete_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		check: (item, value) => {
			return customerPointList.every(v => v[item] !== value);
		}
	}));

	// 確認ダイアログ
	const [open, setOpen] = useState(false);

	const handleDeleteOk = () =>{
		const _data = {
			point_id: del.pointId,
			update_time: del.update_time,
			update_by_user: del.update_by_user,
		};
		setOpen(false);
		ref.current.delete(_data);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleKeyUp = e => {
		if (e.keyCode === 13) {
			handleDeleteOk();
		}
	};

	const handleChangePage = (_event, newPage) => {
		setPage(newPage);
	};
/*
	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};
*/

	let detail = '';
	if (del) {
		detail = [
			"\n",
			`${t('Name')}: ${del.name}`
		].join('\n');
	}

	return (
		<>
			<Box className={classes.flexColumn}>
				<Box className={classes.flexVariable}>
					<TableContainer className={classes.container} ref={tableRef}>
						<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
							<TableHead>
								<TableRow className={classes.tableRow}>
									<TableCell
										key='edit'
										className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
										padding="none"
									>
									</TableCell>
									{columns.map((column, _i) => (
										column.label && <TableCell
											key={column.id}
											align={column.align}
											style={{ minWidth: 0 }}
											className={clsx(classes.headerCell, classes.tableCell)}
											{...column.colSpan ? {colSpan: column.colSpan} : ''}
										>
											{t(column.label)}
										</TableCell>
									))}
									<TableCell
										key='delete'
										className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
										padding="none"
									>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{list.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((row) => {
									return (
										<TableRow hover role="checkbox" tabIndex={-1} key={row.pointId}>
											<TableCell key='editBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
												<RoleControl subject={SUBJ_POINT_AREA_SETTING_LIST} verb={VERB_UPDATE} >
													<IconButton aria-label="edit" size="small" className={classes.smallBtn} onClick={() => handleEdit(row)}>
														<Tooltip title="edit" arrow >
															<PencilIcon fontSize="small" />
														</Tooltip>
													</IconButton>
												</RoleControl>
											</TableCell>
											{columns.map((column, _i) => {
												const value = row[column.id];
												return (
													<TableCell
														key={column.id}
														align={column.align}
														className={clsx(classes.tableCell, ['lat', 'lon'].includes(column.id) && classes.newline)}
													>
														{
															['lat', 'lon'].includes(column.id) ?
																column.format(row['position']) :
															column.id === 'iconName' ?
																row['iconId'] <= 100 ? t(POINT_DEFAULT_ICONS.find(v => v.iconId === row['iconId'])?.name) : value :
																column.format ? column.format(value, t) : value
														}
													</TableCell>
												);
											})}
											<TableCell key='deleteBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
												<RoleControl subject={SUBJ_POINT_AREA_SETTING_LIST} verb={VERB_DELETE} >
													<IconButton aria-label="delete" size="small" className={classes.smallBtn} onClick={() => handleDelete(row)}>
														<Tooltip title="delete" arrow >
															<DeleteIcon fontSize="small" />
														</Tooltip>
													</IconButton>
												</RoleControl>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
				<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '2px', alignItems: 'baseline', fontSize: '0.875rem' }}>
					{/* <TablePagination
						component="Box"
						count={list.length}
						rowsPerPage={rowsPerPage}
						rowsPerPageOptions={[]}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						ActionsComponent={FirstLastPaginationActions}
						className={classes.pagination}
					/> */}
					<div>{`${page * rowsPerPage + 1} - ${Math.min((page + 1) * rowsPerPage, list.length)} / ${list.length}`}</div>
					<Pagination
						page={page+1}
						count={Math.ceil(list.length / rowsPerPage)}
						onChange={(event, value) => handleChangePage(event, value - 1)}
					/>
				</Box>
			</Box>
			<Dialog open={open} onClose={handleClose} onKeyUp={handleKeyUp}>
				<DialogContent>
					<DialogContentText className={classes.newline}>{t('COMPANY_POINT_DELETE_CONFIRM', {detail})}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">Cancel</Button>
					<Button onClick={handleDeleteOk} color="primary">Ok</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

function PointAreaList() {
	const tableRef = useRef(null);
	const childRef = useRef({});
	const inputFilterRef = useRef(null);
	const [formData, setFormData] = useState({});
	const [filterWord, setFileterWord] = useState('');
	const [open, setOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [waiting, setWaiting] = useState(false);
	const timeZone = useRecoilValue(timeZoneSelector);

	const inputRefs = formInputList.reduce((a, v) => (a[v.code] = createRef()) && a, {});

	const { control, formState: { errors, isDirty, isValid }, getValues, reset, watch, trigger } = useForm({
		mode: 'onChange',
	});
	const { fields, remove, insert } = useFieldArray({
		control,
		name: "positions",
		rules: {
			minLength: 3,
			required: true,
		},
	});

	const watchType = watch('type');
	const watchConcentricCircles = watch('concentricCircles');
	const watchIconTermBegin = watch('iconTermBegin');

	useEffect(() => {
		// trigger();
		trigger([
			'iconId',
			'iconDisplay',
			'concentricCircles',
			'concentricCircles1',
			'concentricCircles2',
			'concentricCircles3',
			'lat',
			'lon',
			'positions',
			'radius',
		]);
	}, [watchType, trigger]);

	useEffect(() => {
		trigger([...Array(3)].map((v, i) => `concentricCircles${i + 1}`));
	}, [watchConcentricCircles, trigger]);

	useEffect(() => {
		trigger('iconTermEnd');
	}, [watchIconTermBegin, trigger]);

	const classes = useStyles();
	const {t} = useTranslation();

	const handleNew = () => {
		const _formDefault = {
			...formDefault,
			positions: [...Array(3)].map(_ => ({lat: null, lon: null})),
			iconTermBegin: FPEUtils.formatTimeZoneDate(Date.now(), timeZone, 'YYYY-MM-DDTHH:mm'),
			iconTermEnd: FPEUtils.formatTimeZoneDate('2100/12/31 00:00Z', timeZone, 'YYYY-MM-DDTHH:mm'),
		}
		setEditMode(false);
		reset(_formDefault);
		setFormData({});
		setOpen(true);
	}

	const handleEdit = useCallback((row) => {
		setEditMode(true);
		const _formData = Object.assign({}, row, {
		});

		//形式
		formInputList.forEach(v => v.type === 'radio' && (_formData[v.code] += ""));

		//色
		_formData.color = `#${_formData.color}`;

		//同心円
		if (_formData.type === "1" && _formData.concentricCircles) {
			for (let i = 0; i < 3; i++) {
				if (_formData.concentricCircles.length <= i) break;
				_formData[`concentricCircles${i + 1}`] = String(formatNM(_formData.concentricCircles[i]));
			}
			_formData.concentricCircles = true;
		}

		//緯度経度
		if (_formData.type === "3") {
			const positions = _formData.position.slice(0, -1);
			_formData.positions = positions.map(v => ({lon: v[0], lat: v[1]}));
		} else {
			_formData.lat = `${_formData.position[1]}`;
			_formData.lon = `${_formData.position[0]}`;
		}

		//半径
		if (_formData.type === "2") {
			_formData.radius = String(formatNM(_formData.radius));
		}

		["iconTermBegin", "iconTermEnd"].forEach(v => {
			_formData[v] = FPEUtils.formatTimeZoneDate(_formData[v], timeZone, 'YYYY-MM-DDTHH:mm')
		});

		Object.keys(formDefault).forEach(key => (_formData[key] === null || _formData[key] === undefined) && (_formData[key] = formDefault[key]));
		reset(_formData);
		setFormData(_formData);
		setOpen(true);
	}, [reset, timeZone]);

	const handleSearch = () => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleDeleteFilter = () => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleClose = () => {
		setOpen(false);
	}

	const handleClick = (id, plus) => {
		if (plus) {
			insert(id + 1, { lat: null, lon: null });
		} else {
			remove(id);
		}
	}

	const handleSave = () => {
		const data = getValues();
		const common = [
			"name",
			"type",
			// "color",
			"labelDisplay",
		];

		const _data = {};
		switch(data.type) {
			case "1":
				common.push(...[
					"iconId",
					"iconDisplay",
				]);
				if (data.concentricCircles) {
					_data.concentricCircles = [
						data.concentricCircles1,
						data.concentricCircles2,
						data.concentricCircles3,
					].filter(v => !!v).sort((a, b) => a - b).map(v => Math.round(FPEUtils.nm2m(v)));
				} else {
					_data.concentricCircles = null;
				}
				_data.position = [
					data.lon - 0,
					data.lat - 0,
				];
				break;
			case "2":
				_data.radius = Math.round(FPEUtils.nm2m(data.radius));
				_data.position = [
					data.lon - 0,
					data.lat - 0,
				];
				break;
			case "3":
				_data.position = data.positions
					.map(v => [v.lon, v.lat])
					.filter(v => !!v[0] && !!v[1])
					.map(v => [v[0] - 0, v[1] - 0])
				;
				break;
			default:
				break;
		}

		_data.color = data.color.slice(1);

		common.forEach(v => {
			if (v === 'type') {
				_data[v] = data[v] | 0;
			} else {
				_data[v] = data[v]
			}
		});
		["iconTermBegin", "iconTermEnd"].forEach(v => {
			_data[v] = moment.tz(data[v], timeZone).tz('utc').format('YYYY-MM-DDTHH:mm:ss[Z]');
		});

		if (!editMode) {
			childRef.current.register(_data);
		} else {
/* 一旦差分検出はなし
			const diff = Object.keys(data).reduce((previousValue, currentValue) => {
				if (formData[currentValue] !== data[currentValue]) {
					if (Array.isArray(formData[currentValue]) && Array.isArray(data[currentValue]) ) {
						// previousValue[currentValue] = {
						// 	delete: formData[currentValue].filter(v => !data[currentValue].includes(v)),
						// 	register: data[currentValue].filter(v => !formData[currentValue].includes(v)),
						// }
						if (!FPEUtils.isEqualObject(formData[currentValue], data[currentValue])) {
							previousValue[currentValue] = data[currentValue];
						}
					} else {
						previousValue[currentValue] = data[currentValue];
					}
				}
				return previousValue;
			}, {});

			let _data = null;
			if (Object.keys(diff).length > 0) {
				_data = {
					[formData.pointId]: diff
				};
			}
*/
			_data.pointId = formData.pointId;
			_data.update_time = formData.update_time;
			_data.update_by_user = formData.update_by_user;
			childRef.current.update(_data);
		}
		setOpen(false);
	}

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
				<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(); event.preventDefault(); }}>
					<div className={classes.searchIcon}>
						<SearchIcon />
					</div>
					<InputBase
						className={classes.input}
						placeholder={`${t('Name')}、${t('point_area_type')}、${t('Icon')}...`}
						inputProps={{ 'aria-label': 'filter aircraft group list' }}
						inputRef={inputFilterRef}
						// onChange={handleChange}
					/>
					{filterWord.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
						<CloseIcon />
					</IconButton>}
				</Paper>
				<RoleControl subject={SUBJ_POINT_AREA_SETTING_LIST} verb={VERB_CREATE}>
					<Button variant="outlined" size="small" className={classes.btn} onClick={handleNew}>{t('New')}</Button>
				</RoleControl>
			</Box>
			<Box style={{ padding: '0 10px', flexGrow: 1, overflow: 'hidden' }}>
				{waiting && <Loading backdrop={true} />}
				<Suspense fallback={<Loading />}>
					<PointAreaListTable filterWord={filterWord} ref={childRef} handleEdit={handleEdit} setWaiting={setWaiting} tableRef={tableRef}/>
				</Suspense>
			</Box>
			<Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
				<DialogContent>
					<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
						<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', marginLeft: '15px' }}>
							<Box style={{ marginLeft: 'auto'}}>
								<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
									<CloseIcon />
								</IconButton>
							</Box>
							<Box>
								<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave} disabled={!(isDirty && isValid)}>{t('save')}</Button>
							</Box>
						</Box>
						<Box style={{ display: 'flex', flexDirection: 'column', paddingBottom: '40px' }}>
							<ul className={classes.formRoot}>
								<li>
									<dl className={classes.dataList}>
										<dt className={classes.dataLabel}>{t('Name')}<span className="required"> * {t('required')}</span></dt>
										<dd><FormText code="name" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></dd>
									</dl>
									<dl className={classes.dataList}>
										<dt className={classes.dataLabel}>{t('point_area_type')}<span className="required"> * {t('required')}</span></dt>
										<dd><FormRadio code="type" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></dd>
									</dl>
									<dl className={clsx(classes.dataList, watchType !== '1' && classes.hide)}>
										<dt className={classes.dataLabel}>{t('Icon')}{watchType === '1' && <span className="required"> * {t('required')}</span>}</dt>
										<dd style={{ display: 'flex' }}>
											<FormIconSelect code="iconId" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} disabled={watchType !== '1'} />
											<FormRadio code="iconDisplay" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} disabled={watchType !== '1'} style={{ marginLeft: '30px' }} />
										</dd>
									</dl>
									<dl className={classes.dataList}>
										<dt className={classes.dataLabel}>{t('Color')}<span className="required"> * {t('required')}</span></dt>
										<dd><FormText code="color" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></dd>
									</dl>
									<dl className={clsx(classes.dataList, watchType !== '1' && classes.hide)}>
										<dt className={classes.dataLabel}>{t('Concentric circles')}</dt>
										<dd style={{ display: 'flex', alignItems: 'baseline' }} className={classes.formSpacer}>
											<FormSwitch code="concentricCircles" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} disabled={watchType !== '1'} />
											<FormText code="concentricCircles1" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} disabled={watchType !== '1' || !watchConcentricCircles} />
											<FormText code="concentricCircles2" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} disabled={watchType !== '1' || !watchConcentricCircles} />
											<FormText code="concentricCircles3" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} disabled={watchType !== '1' || !watchConcentricCircles} />
										</dd>
									</dl>
									<dl className={classes.dataList}>
										<dt className={classes.dataLabel}>{t('Latitude Longitude')}<span className="required"> * {t('required')}</span></dt>
										<dd>
											<>
											{watchType !== '3' ?
												<div className={clsx(classes.formHorizontally, classes.formSpacer)}>
													<Box className={classes.formHorizontally}><Box className={classes.formHeading}>{t('latitude')}</Box><FormLatLon code="lat" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></Box>
													<Box className={classes.formHorizontally}><Box className={classes.formHeading}>{t('longitude')}</Box><FormLatLon code="lon" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></Box>
												</div>
											:
												fields.map((field, i) => (
													<div key={field.id} className={clsx(classes.formHorizontally, classes.formSpacer)} style={{ alignItems: 'start' }}>
														<Box className={classes.formHorizontally}><Box className={classes.formHeading}>{t('latitude')}</Box><FormLatLon code="lat" name={`positions[${i}].lat`} {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></Box>
														<Box className={classes.formHorizontally}><Box className={classes.formHeading}>{t('longitude')}</Box><FormLatLon code="lon" name={`positions[${i}].lon`} {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></Box>
														<Box className={classes.formHorizontally}>
															<IconButton aria-label="remove" disabled={fields.length <= 3} style={{ padding: '4px' }} onClick={() => handleClick(i, false)}>
																<RemoveIcon fontSize="small" />
															</IconButton>
															<IconButton aria-label="add" style={{ padding: '4px' }} onClick={() => handleClick(i, true)}>
																<AddIcon fontSize="small" />
															</IconButton>
														</Box>
													</div>
												))}
											</>
										</dd>
									</dl>
									<dl className={clsx(classes.dataList, watchType !== '2' && classes.hide)}>
										<dt className={classes.dataLabel}>{'Radius'}{watchType === '2' && <span className="required"> * {t('required')}</span>}</dt>
										<dd style={{ display: 'flex' }}>
											<FormText code="radius" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} disabled={watchType !== '2'} />
										</dd>
									</dl>
									<dl className={classes.dataList}>
										<dt className={classes.dataLabel}>{t('Labeling')}<span className="required"> * {t('required')}</span></dt>
										<dd><FormRadio code="labelDisplay" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></dd>
									</dl>
									<dl className={classes.dataList}>
										<dt className={classes.dataLabel}>{t('Period')}({timeZone === 'utc' ? 'UTC' : 'JST'})<span className="required"> * {t('required')}</span></dt>
										<dd className={clsx(classes.formHorizontally, classes.formSpacer)}>
											<Box className={classes.formHorizontally}>
												<Box className={classes.formHeading}>{t('Start')}</Box>
												<FormText
													code="iconTermBegin"
													style={{ width: 'min-content' }}
													{...{inputRefs, childRef, control, getValues, formData, errors, editMode}}
												/>
											</Box>
											<Box className={classes.formHorizontally}>
												<Box className={classes.formHeading}>{t('End')}</Box>
												<FormText
													code="iconTermEnd"
													style={{ width: 'min-content' }}
													{...{inputRefs, childRef, control, getValues, formData, errors, editMode}}
												/>
											</Box>
										</dd>
									</dl>
								</li>
							</ul>
						</Box>
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	)
}

export default PointAreaList;

const makeRules = (item, t, {childRef, getValues, formData}) => {
	const result = {
		required: item.rules.required ? t('validate_required') : false,
	};
	if (item.rules.pattern) {
		result.pattern = {
			value: item.rules.pattern,
			message: t('validate_pattern')
		};
	}
	if (item.rules.maxLength) {
		result.maxLength = {
			value: item.rules.maxLength,
			message: t('validate_max_length', { maxLength: item.rules.maxLength }),
		};
	}
	result.validate = {
		common: value => {
			if (!item.rules.unique) {
				return true;
			}
			if (formData[item.code] === value) {
				return true;
			}
			return childRef.current.check(item.code, value) || t('already exists')
		},
		...Object.fromEntries(Object.entries({...item.rules.validate}).map(([key, func]) => {
			return [key, (value) => func(value, {getValues, t})];
		})),
	};

	return result;
};

const FormText = ({code, name, inputRefs, childRef, control, getValues, formData, errors, editMode, ...prop}) => {

	const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});
	const inputProps = {};
	if (item.type !== 'text') {
		inputProps.type = item.type;
	} else if (item.unit) {
		inputProps.endAdornment = <InputAdornment position="start">{item.unit}</InputAdornment>
	}

	return (
		<Controller
			key={name || item.code}
			control={control}
			rules={rules}
			render={({ field: { name, onChange, onBlur, value } }) => (
				<TextField
					className={classes.formInputText}
					// label={<div>{item.name}{item.rules.required && <span className="required"> * {t('required')}</span>}</div>}
					name={name}
					id={name}
					// variant="outlined"
					size="small"
					InputLabelProps={{
						shrink: true,
					}}
					error={!!errors[name]}
					inputRef={inputRefs[name]}
					InputProps={inputProps}
					helperText={t(errors[name]?.message)}
					onChange={onChange}
					onBlur={onBlur}
					value={value}
					disabled={editMode && item.rules.reedit === false}
					{...prop}
				/>
			)}
			name={name || item.code}
		/>
	);
};

const FormLatLon = ({code, name, inputRefs, childRef, control, getValues, formData, errors, editMode, ...prop}) => {

	const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});
	const inputProps = {};
	if (item.type !== 'text') {
		inputProps.type = item.type;
		inputProps.readOnly = true;
	}

	return (
		<Controller
			key={name || item.code}
			control={control}
			rules={rules}
			render={({ field: { name, onChange, onBlur, value } }) => (
				<Box style={{ display: 'flex', alignItems: 'flex-start' }}>
					<TextField
						autoComplete="off"
						className={clsx(classes.formInputText, classes.formInputLonLat)}
						name={name}
						id={name}
						size="small"
						InputLabelProps={{
							shrink: true,
						}}
						error={!!errors[name]}
						inputRef={inputRefs[name]}
						inputProps={inputProps}
						helperText={t(errors[name]?.message)}
						value={(value !== null && value !== undefined) ? item.code === "lat" ? FPEUtils.formatLatDM(value) : FPEUtils.formatLonDM(value) : ""}
						disabled={editMode && item.rules.reedit === false}
						{...prop}
					/>
					<CoordinateButton
						type={item.code}
						// value={value === "" ? null : value}
						value={value === undefined ? null : value}
						// prevValue={value !== "" ? FPEUtils.formatLatDM(value) : ""}
						disabled={editMode && item.rules.reedit === false}
						handleInputEnd={(before, after) => {
							if (after === "" || before === after) {
								return;
							}
							onChange(after);
						}}
						style={{ position: 'relative', right: 'auto', marginLeft: '-26px' }}
					/>
				</Box>
			)}
			name={name || item.code}
		/>
	);
};

const FormRadio = ({code, inputRefs, childRef, control, getValues, formData, errors, editMode, disabled, ...prop}) => {

	const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});

	return (
		<Controller
			key={item.code}
			control={control}
			rules={rules}
			render={({ field: { name, onChange, onBlur, value } }) => (
				<RadioGroup row aria-label={name} name={name} value={value} onChange={onChange} onBlur={onBlur} className={classes.radioRoot} {...prop}>
					{((Array.isArray(item.data.list) && item.data.list) || (childRef.current[item.data.list] && childRef.current[item.data.list]) || []).map((v) => (
						<FormControlLabel
							key={v[item.data.key]}
							value={v[item.data.key]}
							control={<Radio color="primary" />}
							label={t(v[item.data.name])}
							disabled={!!disabled || (editMode && item.rules.reedit === false)}
						/>
					))}
				</RadioGroup>
			)}
			name={item.code}
		/>
	);
};

const FormSwitch = ({code, inputRefs, childRef, control, getValues, formData, errors, editMode, ...prop}) => {

	// const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});

	return (
		<Controller
			key={item.code}
			control={control}
			rules={rules}
			render={({ field: { name, onChange, onBlur, value } }) => (
				<Switch
					checked={value}
					onChange={onChange}
					color="primary"
					name={name}
					inputProps={{ 'aria-label': 'primary checkbox' }}
					inputRef={inputRefs[name]}
					{...prop}
				/>
			)}
			name={item.code}
		/>
	);
};

const FormIconSelect = ({code, inputRefs, childRef, control, getValues, formData, errors, editMode, ...prop}) => {

	const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});

	return (
		<Controller
			name={item.code}
			control={control}
			rules={rules}
			render={({ field: { name, onChange, onBlur, value } }) => (
				<>
					<Select
						labelId={`${item.code}-checkbox-label`}
						id={`${name}-checkbox`}
						style={{ minWidth: '50px' }}
						value={value}
						inputRef={inputRefs[name]}
						onChange={onChange}
						onBlur={onBlur}
						input={<Input id={`${name}-select`} />}
						MenuProps={MenuProps}
						{...prop}
					>
						{((Array.isArray(item.data.list) && item.data.list) || (childRef.current[item.data.list] && childRef.current[item.data.list]) || []).map((v) => (
							<MenuItem dense key={v[item.data.key]} value={v[item.data.key]} className={classes.menuItem}>
								<ListItemIcon>
									{(v.image && <SvgIcon viewBox="0 0 512 512" style={{ width: '32px', height:'32px' }}>{v.image}</SvgIcon>) || <img src={v.path} alt={v.name} />}
								</ListItemIcon>
								<ListItemText primary={v[item.data.name]} className={classes.menuItemText} />
							</MenuItem>
						))}
					</Select>
				</>
			)}
		/>
	);
};
